import { useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Star } from "lucide-react";
import { IMAGES } from "@/lib/constants";

const Counter = ({ target, suffix = "", prefix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1500;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, target]);

  const display = Number.isInteger(target) ? Math.round(val) : val.toFixed(1);
  return <span ref={ref}>{prefix}{display}{suffix}</span>;
};

const stats = [
  { value: <><Star size={20} fill="#C9A84C" stroke="#C9A84C" className="inline -mt-1 mr-1" /><Counter target={4.9} /></>, label: "Google Bewertung" },
  { value: <><Counter target={100} suffix="%" /></>, label: "Zufriedene Kunden" },
  { value: <><Counter target={1} /></>, label: "Standort. Fokus. Qualität." },
];

export default function Story() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["6%", "-6%"]);

  return (
    <section id="ueber-uns" ref={ref} className="py-28 lg:py-36" style={{ background: "#111111" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
          className="relative"
        >
          <div className="gold-frame">
            <motion.div style={{ y: imgY }} className="overflow-hidden">
              <img
                src={IMAGES.portrait}
                alt="Nabil – Inhaber des Nabil Barbershop"
                className="w-full aspect-square object-cover"
                data-testid="story-portrait"
              />
            </motion.div>
          </div>
          <div
            className="absolute -bottom-5 left-8 px-5 py-3 text-sm font-medium"
            style={{ background: "#C9A84C", color: "#080808", letterSpacing: "1px" }}
            data-testid="story-owner-badge"
          >
            INHABER · NABIL
          </div>
        </motion.div>

        <div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="pre-headline mb-6"
          >
            Über uns
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl mb-8"
            data-testid="story-headline"
          >
            Mehr als ein <span className="gold-text">Haarschnitt.</span>
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="space-y-5 text-base leading-relaxed"
            style={{ color: "#999999" }}
          >
            <p>
              Im Nabil Barbershop dreht sich alles um einen Mann: <span style={{ color: "#F5F0E8" }}>dich</span>. Wir schneiden nicht einfach Haare — wir formen deinen Auftritt. Mit Präzision, Erfahrung und dem richtigen Gespür für Style.
            </p>
            <p>
              Seit unserer Eröffnung in Bochum-Wattenscheid sind wir die erste Adresse für Männer, die auf ihr Erscheinungsbild Wert legen. <span className="font-display italic" style={{ color: "#E8CC7A" }}>Modern. Entspannt. Auf höchstem Niveau.</span>
            </p>
          </motion.div>

          <div className="grid grid-cols-3 gap-6 mt-12">
            {stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 + i * 0.15 }}
                className="border-l pl-4"
                style={{ borderColor: "rgba(201,168,76,0.4)" }}
                data-testid={`story-stat-${i}`}
              >
                <div className="font-display text-2xl sm:text-3xl font-bold gold-text">{s.value}</div>
                <div className="text-xs mt-2" style={{ color: "#999999" }}>{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
